import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import {
  LayoutDashboard, FileText, GanttChart, Clock, Users, Scale, Smartphone,
  Microscope, BookMarked, Shield, Gavel, ListChecks, Sparkles, FileOutput, Settings,
  CreditCard, ScrollText, Landmark,
} from "lucide-react";

const ITEMS = [
  { key: "dashboard", to: "/", icon: LayoutDashboard },
  { key: "case_overview", to: "/case", icon: FileText },
  { key: "procedure", to: "/procedure", icon: GanttChart },
  { key: "timeline", to: "/timeline", icon: Clock },
  { key: "witnesses", to: "/witnesses", icon: Users },
  { key: "evidence", to: "/evidence", icon: Scale },
  { key: "digital_evidence", to: "/digital-evidence", icon: Smartphone },
  { key: "forensics", to: "/forensics", icon: Microscope },
  { key: "case_law", to: "/case-law", icon: BookMarked },
  { key: "defense_theory", to: "/defense-theory", icon: Shield },
  { key: "hearings", to: "/hearings", icon: Gavel },
  { key: "tasks", to: "/tasks", icon: ListChecks },
  { key: "ai_prompts", to: "/ai-prompts", icon: Sparkles },
  { key: "reports", to: "/reports", icon: FileOutput },
  { key: "courtroom", to: "/courtroom", icon: Landmark },
  { key: "billing", to: "/billing", icon: CreditCard, group: "admin" },
  { key: "audit_log", to: "/audit-log", icon: ScrollText, group: "admin" },
  { key: "settings", to: "/settings", icon: Settings, group: "admin" },
];

export default function Sidebar() {
  const { t } = useApp();
  const loc = useLocation();
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden md:flex w-[260px] shrink-0 flex-col border-r border-border/70 glass"
    >
      <div className="px-5 pt-6 pb-4 border-b border-border/60">
        <div className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <div className="size-9 rounded-lg flex items-center justify-center bg-[hsl(var(--gold)/0.12)] border gold-border">
            <Scale className="size-5 gold-text" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-heading font-semibold tracking-tight text-[15px] leading-none">ΙΧΝΟΔΙΚΗΣ</div>
            <div className="text-[9.5px] text-muted-foreground mt-1 leading-tight">κάθε συμπέρασμα, δεμένο στο ίχνος του</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2.5">
        {ITEMS.map(({ key, to, icon: Icon, group }, idx) => {
          const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
          const prevGroup = idx > 0 ? ITEMS[idx-1].group : null;
          const showDivider = group === "admin" && prevGroup !== "admin";
          return (
            <React.Fragment key={key}>
              {showDivider && <div className="my-2 mx-3 border-t border-border/40"/>}
              <NavLink
                to={to} end={to === "/"}
                data-testid={`sidebar-nav-${key.replace(/_/g, "-")}`}
                className={`group flex items-center gap-3 px-3 py-2 my-0.5 rounded-md text-[13.5px] transition-all duration-200
                  ${active
                    ? "bg-[hsl(var(--gold)/0.12)] text-foreground border border-[hsl(var(--gold)/0.25)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))/0.6] border border-transparent"}`}
              >
                <Icon className={`size-4 ${active ? "gold-text" : ""}`} strokeWidth={1.5} />
                <span className="font-medium">{t(key)}</span>
                {active && <span className="ml-auto size-1.5 rounded-full bg-[hsl(var(--gold))]" />}
              </NavLink>
            </React.Fragment>
          );
        })}
      </nav>
      <div className="px-4 py-3 border-t border-border/60 text-[10px] text-muted-foreground font-mono">
        v1.0 · production
      </div>
    </aside>
  );
}
